import { useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { PenTool, Upload } from 'lucide-react'
import { AppShell, Crumb, HomeCrumb } from '../components/AppShell'
import { SkeletonScreen } from '../components/Skeleton'
import { useCheckCatalogue, useCountries, useReferenceLayers, useSuppliers } from '../api/hooks'
import { tokens } from '../theme'
import { UploadRun } from './newrun/UploadRun'
import { DrawRun } from './newrun/DrawRun'

export type RunMode = 'upload' | 'draw'

/** The "Upload file / Draw on map" switch that moves between 1a and 2a. */
export function ModeSwitch({
  mode, onChange, note,
}: { mode: RunMode; onChange: (m: RunMode) => void; note?: string }) {
  const item = (value: RunMode, label: string, Icon: typeof Upload) => {
    const active = mode === value
    return (
      <Box
        component="button"
        type="button"
        onClick={() => onChange(value)}
        aria-pressed={active}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, border: 0, cursor: 'pointer',
          borderRadius: `${tokens.radius.sm}px`, font: `500 13px ${tokens.font.sans}`,
          backgroundColor: active ? 'background.paper' : 'transparent',
          boxShadow: active ? tokens.shadow[1] : 'none',
          color: active ? 'text.primary' : 'text.secondary',
          transition: `background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
        }}
      >
        <Icon size={14} strokeWidth={1.5} />
        {label}
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <Box sx={{ display: 'flex', p: 0.75, borderRadius: `${tokens.radius.md}px`, backgroundColor: tokens.color.ink100 }}>
        {item('upload', 'Upload file', Upload)}
        {item('draw', 'Draw on map', PenTool)}
      </Box>
      {note && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{note}</Typography>
      )}
    </Box>
  )
}

export function NewRun() {
  // ?mode=draw — a farmer's "Draw a field" lands straight on the map.
  const [params] = useSearchParams()
  const [mode, setMode] = useState<RunMode>(params.get('mode') === 'draw' ? 'draw' : 'upload')

  const catalogue = useCheckCatalogue()
  const { data: layers = [] } = useReferenceLayers()
  const { data: suppliers = [] } = useSuppliers()
  const { data: countries = [] } = useCountries()

  const crumbs = [
    <HomeCrumb key="a" />,
    <Crumb key="b">Add polygons</Crumb>,
    <Crumb key="c" muted>New run</Crumb>,
  ]

  if (catalogue.isLoading || !catalogue.data) {
    return (
      <AppShell crumbs={crumbs}>
        <SkeletonScreen />
      </AppShell>
    )
  }

  const { checks, default_config: defaultConfig } = catalogue.data

  // 2a is map-first and uses the full width, as the mockup shows.
  if (mode === 'draw') {
    return (
      <AppShell crumbs={crumbs} rail={false}>
        <DrawRun
          checks={checks}
          layers={layers}
          suppliers={suppliers}
          countries={countries}
          defaultConfig={defaultConfig}
          switcher={
            <ModeSwitch
              mode={mode}
              onChange={setMode}
              note="For a handful of plots. Checks run as you close each shape."
            />
          }
        />
      </AppShell>
    )
  }

  return (
    <AppShell crumbs={crumbs}>
      <UploadRun
        checks={checks}
        layers={layers}
        countries={countries}
        defaultConfig={defaultConfig}
        switcher={
          <ModeSwitch
            mode={mode}
            onChange={setMode}
            note="One file is one country; it may name several suppliers."
          />
        }
      />
    </AppShell>
  )
}
